import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Toast from "@/components/base/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: "ri-film-line",
    title: "作品档案",
    text: "为每部电影与剧集建立完整档案：主创、类型、地区、年份、时长与剧情简介，信息一眼看全。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    text: "类型、地区、年份、热度自由组合，配合排序与加载更多，快速缩小选择范围。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯聚合",
    text: "行业动态、深度影评与幕后花絮按时间与热度整理，作品与资讯互相链接。",
  },
  {
    icon: "ri-stack-line",
    title: "主题片单",
    text: "按氛围与主题策划专题，把散落的作品组织成一条可以连续观看的线索。",
  },
];

const steps = [
  {
    title: "从首页找到方向",
    text: "焦点轮播与热度榜展示近期重点内容，快捷入口可直达电影、剧集、资讯与专题。",
  },
  {
    title: "用筛选缩小范围",
    text: "在分类页组合类型、地区与年份，或直接输入关键词搜索片名、主创与题材。",
  },
  {
    title: "收藏与分享",
    text: "详情页可一键收藏（保存在本机浏览器）或复制链接，方便之后回看与推荐给朋友。",
  },
];

const stats = [
  { value: "1,200+", label: "作品档案" },
  { value: "42", label: "类型标签" },
  { value: "12", label: "主题专题" },
  { value: "每日", label: "资讯更新" },
];

const socials = [
  { icon: "ri-weibo-line", label: "微博", account: "@天马传媒", href: "https://rfduk.cn" },
  { icon: "ri-wechat-line", label: "微信公众号", account: "天马传媒影视", href: "https://mp.weixin.qq.com" },
  { icon: "ri-bilibili-line", label: "哔哩哔哩", account: "天马影视观察", href: "https://www.bilibili.com" },
];

const CONTACT_EMAIL = "hello@tianmamedia.example";

export default function About() {
  const [toast, setToast] = useState("");

  usePageMeta(
    "关于我们 - 天马传媒影视内容聚合平台",
    "了解天马传媒：一个专注影视作品档案、行业资讯与主题片单的内容聚合平台，提供多维筛选与专题化浏览体验。",
    "关于天马传媒,影视内容平台,影视资讯,平台介绍",
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setToast("邮箱已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动选择邮箱地址");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200 bg-background-100">
          <div className="shell grid gap-8 py-10 md:grid-cols-[1.1fr_0.9fr] md:items-center md:py-14">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
                <Link to="/" className="cursor-pointer transition hover:text-primary-600">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-300"></i>
                <span className="text-foreground-800">关于我们</span>
              </nav>
              <p className="mt-5 flex items-center gap-2 text-xs tracking-[0.24em] text-accent-600">
                <span className="h-px w-6 bg-current"></span>
                ABOUT TIANMA MEDIA
              </p>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                关于天马传媒
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                天马传媒是一个专注影视内容聚合的平台。我们不做播放，也不做下载，只把一部作品该有的信息整理清楚：谁拍的、讲什么、值不值得看，以及它最近有什么新动态。
              </p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                从 2019 年开始，我们持续整理电影与剧集档案，并尝试用主题专题的方式，把作品放回它们所属的语境里。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/category/movie"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
                >
                  开始浏览作品
                </Link>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-mail-line"></i>
                  复制联系邮箱
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200 bg-primary-900">
              <div className="aspect-[16/10] w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20photograph%20of%20an%20empty%20retro%20cinema%20auditorium%20with%20amber%20wall%20lights%20and%20deep%20navy%20seats%2C%20soft%20projector%20beam%20cutting%20through%20dusty%20air%2C%20quiet%20cinematic%20atmosphere%2C%20no%20people&width=1200&height=750&seq=tm-about-cinema&orientation=landscape"
                  alt="天马传媒影视资料室放映厅"
                  title="天马传媒 - 影视内容聚合平台"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-background-200">
          <div className="shell grid grid-cols-2 gap-4 py-8 md:grid-cols-4 md:py-10">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center md:text-left">
                <p className="font-heading text-2xl font-semibold text-primary-600 md:text-3xl">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="shell py-12 md:py-16">
          <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
            <span className="h-px w-6 bg-current"></span>
            我们提供什么
          </p>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-foreground-950">
            四个核心服务
          </h2>
          <div className="mt-7 grid gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
            {services.map((service) => (
              <div
                key={service.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${service.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {service.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{service.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="shell py-12 md:py-16">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
              <div>
                <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                  <span className="h-px w-6 bg-current"></span>
                  使用说明
                </p>
                <h2 className="mt-3 font-heading text-2xl font-semibold text-foreground-950">
                  三步找到想看的
                </h2>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-foreground-600">
                  本站所有内容均为演示数据，播放与下载功能不在此站点提供。收藏状态保存在本机浏览器，不会上传。
                </p>
              </div>
              <ol className="space-y-4">
                {steps.map((step, index) => (
                  <li
                    key={step.title}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-semibold text-accent-800">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-950">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {step.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="shell py-12 md:py-16">
          <div className="grid gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-[1.1fr_1fr] md:p-8">
            <div>
              <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-current"></span>
                联系我们
              </p>
              <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                内容合作与反馈
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
                如果你发现内容信息有误，或希望推荐作品、洽谈内容合作，欢迎通过邮件联系我们。工作日两个工作日内回复。
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 transition hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-mail-line text-primary-600"></i>
                  {CONTACT_EMAIL}
                </a>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
                >
                  复制邮箱
                </button>
              </div>
            </div>

            <div className="rounded-md bg-background-100 p-5">
              <h3 className="text-sm font-medium text-foreground-950">关注我们</h3>
              <ul className="mt-3 space-y-2.5">
                {socials.map((social) => (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 transition hover:border-primary-300"
                    >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${social.icon} text-base`}></i>
                    </span>
                      <span className="flex-1 text-xs text-foreground-600">{social.label}</span>
                      <span className="text-xs text-foreground-500">{social.account}</span>
                      <i className="ri-arrow-right-up-line text-xs text-foreground-300"></i>
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] leading-relaxed text-foreground-500">
                以上账号与邮箱均为演示信息，仅用于展示平台的沟通方式。
              </p>
            </div>
          </div>

          <p className="mt-8 rounded-md border border-dashed border-background-300 bg-background-100/60 p-4 text-xs leading-relaxed text-foreground-500">
            版权与免责声明：本站为影视内容展示与资讯聚合演示站，不提供任何影片的在线播放、下载或资源分享服务。页面中的片名、主创、评分、资讯均为虚构演示数据，如有雷同纯属巧合。
          </p>
        </section>
      </main>
      <SiteFooter />
      <Toast message={toast} onClose={() => setToast("")} />
    </div>
  );
}